import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { UseFormRegister, FieldErrors, UseFormTrigger, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThemedDatePicker } from '../common/themed-datepicker.component';
import { ATTACHMENT_ACCEPT } from '../../utils/attachment.utils';

interface TravelExpensesFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  selectedFiles: File[];
  totalWithVat: string;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
}

const TravelExpensesForm: React.FC<TravelExpensesFormProps> = ({
  register,
  errors,
  trigger,
  setValue,
  selectedFiles,
  totalWithVat,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData
}) => {
  const { t } = useTranslation();

  // Dropdown states
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [vatDropdownOpen, setVatDropdownOpen] = useState(false);
  const [vatDropdownPosition, setVatDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Dropdown refs
  const isPdfAttachedDropdownRef = React.useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = React.useRef<HTMLDivElement>(null);
  const vatDropdownRef = React.useRef<HTMLDivElement>(null);
  const vatDropdownPortalRef = React.useRef<HTMLDivElement>(null);

  // Options
  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const vatOptions = [
    { value: '', label: t('SELECT_VAT') },
    { value: '0', label: t('VAT_0') },
    { value: '6', label: t('VAT_6') },
    { value: '12', label: t('VAT_12') },
    { value: '25', label: t('VAT_25') }
  ];

  // Get label helpers
  const getYesNoLabel = (value: string) => {
    const option = yesNoOptions.find(opt => opt.value === value);
    return option?.label || t('YES');
  };

  const getVatLabel = (value: string) => {
    if (!value || value === '') {
      return t('SELECT_VAT');
    }
    const option = vatOptions.find(opt => opt.value === value);
    return option?.label || t('SELECT_VAT');
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      // Check if click is outside VAT dropdown trigger AND portal
      if (vatDropdownRef.current && !vatDropdownRef.current.contains(event.target as Node) &&
          vatDropdownPortalRef.current && !vatDropdownPortalRef.current.contains(event.target as Node)) {
        setVatDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate VAT dropdown position
  const calculateVatDropdownPosition = () => {
    if (vatDropdownRef.current) {
      const rect = vatDropdownRef.current.getBoundingClientRect();
      setVatDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Update dropdown position on scroll
  useEffect(() => {
    if (vatDropdownOpen) {
      const handleScroll = () => {
        calculateVatDropdownPosition();
      };

      // Add scroll listener to window and all parent elements
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [vatDropdownOpen]);

  return (
    <>
      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-travel-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-travel-pdf" 
              accept={ATTACHMENT_ACCEPT}
              onChange={onFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} 
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                   <a
                    href={URL.createObjectURL(file)}
                    download={file.name}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#7c3aed',
                    }}
                  >
                    {file.name}
                  </a>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show PDF to Customer - Always Visible */}
       {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div className="custom-dropdown" ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${isPdfAttachedDropdownOpen ? 'open' : ''}`}
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${isPdfAttachedDropdownOpen ? '#7c3aed' : '#eaebf0'}`,
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: '#252525'
              }}>
                {getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}
              </span>
              <svg
                className={`dropdown-arrow ${isPdfAttachedDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isPdfAttachedDropdownOpen && (
              <div
                className="dropdown-list"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}
              >
                <div className="dropdown-content">
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${(formData?.isPdfAttachedToInvoice || 'yes') === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        onFormChange('isPdfAttachedToInvoice', option.value);
                        trigger('isPdfAttachedToInvoice');
                        setIsPdfAttachedDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4efff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                      onMouseLeave={(e) => {
                        if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 1: Show on Invoice & Travel Date */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('SHOW_ON_INVOICE_QUESTION')}</label>
          <div className="custom-dropdown" ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${showOnInvoiceDropdownOpen ? 'open' : ''}`}
              onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${showOnInvoiceDropdownOpen ? '#7c3aed' : '#eaebf0'}`,
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: '#252525'
              }}>
                {getYesNoLabel(formData?.showOnInvoice || 'yes')}
              </span>
              <svg
                className={`dropdown-arrow ${showOnInvoiceDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {showOnInvoiceDropdownOpen && (
              <div
                className="dropdown-list"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}
              >
                <div className="dropdown-content">
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${(formData?.showOnInvoice || 'yes') === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        onFormChange('showOnInvoice', option.value);
                        trigger('showOnInvoice');
                        setShowOnInvoiceDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4efff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                      onMouseLeave={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>{t('TRAVEL_DATE')}</label>
          <ThemedDatePicker
            value={formData?.travelDate || ''}
            onChange={(v) => {
              onFormChange('travelDate', v);
              setValue('travelDate', v, { shouldValidate: true });
              trigger('travelDate');
            }}
            hasError={!!errors.travelDate}
          />
          <input type="hidden" {...register('travelDate', { required: t('PLEASE_SELECT_TRAVEL_DATE') })} />
          {errors.travelDate && (
            <div className="error-message">{errors.travelDate.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 2: VAT & Amount */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('VAT')}</label>
          <div className="custom-dropdown" ref={vatDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${vatDropdownOpen ? 'open' : ''}`}
              onClick={() => {
                if (!vatDropdownOpen) {
                  calculateVatDropdownPosition();
                }
                setVatDropdownOpen(!vatDropdownOpen);
              }}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${errors.vat ? '#dc3545' : (vatDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: vatDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: (formData?.vat && formData?.vat !== '') ? '#252525' : '#68727d'
              }}>
                {getVatLabel(formData?.vat || '')}
              </span>
              <svg
                className={`dropdown-arrow ${vatDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: vatDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {vatDropdownOpen && vatDropdownPosition && ReactDOM.createPortal(
              <div
                ref={vatDropdownPortalRef}
                className="dropdown-list"
                style={{
                  position: 'fixed',
                  top: `${vatDropdownPosition.top}px`,
                  left: `${vatDropdownPosition.left}px`,
                  width: `${vatDropdownPosition.width}px`,
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 9999
                }}
              >
                <div className="dropdown-content">
                  {vatOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${formData?.vat === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        onFormChange('vat', option.value);
                        trigger('vat');
                        setVatDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: formData?.vat === option.value ? '#f4efff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                      onMouseLeave={(e) => {
                        if (formData?.vat !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>,
              document.body
            )}
          </div>
          {errors.vat && (
            <div className="error-message">{errors.vat.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('AMOUNT_SEK_EX_VAT')}</label>
          <input
            type="number"
            min="0"
            {...register('amountExVat', { required: t('PLEASE_ENTER_AMOUNT') })}
            className={`form-control ${errors.amountExVat ? 'error' : ''}`}
            placeholder={t('ENTER_AMOUNT')}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || parseFloat(value) >= 0) {
                onFormChange('amountExVat', value);
                trigger('amountExVat');
              }
            }}
          />
          {errors.amountExVat && (
            <div className="error-message">{errors.amountExVat.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Total Section */}
      <div className="summary-section">
        <div className="summary-item">
          <span className="summary-label">{t('TOTAL_INC_VAT')}</span>
          <span className="summary-value">{totalWithVat} {t('SEK')}</span>
        </div>
      </div>
    </>
  );
};

export default TravelExpensesForm;
